import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MicIcon, FileTextIcon, CheckCircle2Icon, UploadCloudIcon, SparklesIcon, CircleIcon, ChevronRightIcon, CircleDotIcon, CameraIcon, ArrowUpIcon, XIcon } from 'lucide-react';
type Phase = 'idle' | 'voice' | 'document' | 'success';
const PHASE_DURATIONS: Record<Phase, number> = {
  idle: 1200,
  voice: 4000,
  document: 3500,
  success: 1800
};
const WaveformBars = () => {
  const barCount = 12;
  return <div className="flex items-center justify-center gap-[2px] h-4" data-id="element-281">
      {Array.from({
      length: barCount
    }).map((_, i) => <motion.div key={i} className="w-[2px] rounded-full bg-orange-500" animate={{
      height: [3, 8 + Math.random() * 8, 4, 10 + Math.random() * 6, 3]
    }} transition={{
      duration: 0.5 + Math.random() * 0.3,
      repeat: Infinity,
      repeatType: 'mirror',
      delay: i * 0.05,
      ease: 'easeInOut'
    }} data-id="element-282" />)}
    </div>;
};
const TypingText = ({
  text,
  className
}: {
  text: string;
  className?: string;
}) => {
  const [displayed, setDisplayed] = useState('');
  useEffect(() => {
    setDisplayed('');
    let i = 0;
    const interval = setInterval(() => {
      if (i < text.length) {
        setDisplayed(text.slice(0, i + 1));
        i++;
      } else {
        clearInterval(interval);
      }
    }, 55);
    return () => clearInterval(interval);
  }, [text]);
  return <span className={className} data-id="element-283">
      {displayed}
      {displayed.length < text.length && <motion.span className="inline-block w-[1.5px] h-[14px] bg-orange-500 ml-[1px] align-middle" animate={{
      opacity: [1, 0]
    }} transition={{
      duration: 0.5,
      repeat: Infinity,
      repeatType: 'mirror'
    }} data-id="element-284" />}
    </span>;
};
type FieldStatus = 'empty' | 'active' | 'filling' | 'done';
const SidebarFieldItem = ({
  label,
  status,
  isActive
}: {
  label: string;
  status: FieldStatus;
  isActive: boolean;
}) => {
  return <div className={`flex items-center gap-2.5 px-3 py-1.5 rounded-md text-[13px] transition-colors duration-300 ${isActive ? 'bg-orange-50 text-gray-900 font-medium' : 'text-gray-500'}`} data-id="element-285">
      {status === 'done' ? <motion.div initial={{
      scale: 0
    }} animate={{
      scale: 1
    }} transition={{
      type: 'spring',
      stiffness: 400,
      damping: 15
    }} data-id="element-286">
          <CheckCircle2Icon size={14} className="text-green-500 flex-shrink-0" data-id="element-287" />
        </motion.div> : status === 'active' || status === 'filling' ? <motion.div animate={{
      scale: [1, 1.2, 1]
    }} transition={{
      duration: 1.5,
      repeat: Infinity
    }} data-id="element-288">
          <CircleDotIcon size={14} className="text-orange-500 flex-shrink-0" data-id="element-289" />
        </motion.div> : <CircleIcon size={14} className="text-gray-300 flex-shrink-0" data-id="element-290" />}
      <span className="truncate" data-id="element-291">{label}</span>
    </div>;
};
export function MultiModalAnimation() {
  const [phase, setPhase] = useState<Phase>('idle');
  const [voiceStep, setVoiceStep] = useState(0);
  const [docStep, setDocStep] = useState(0);
  const fieldRefs = useRef<(HTMLDivElement | null)[]>([null, null, null]);
  const successRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const resetAndLoop = useCallback(() => {
    setPhase('idle');
    setVoiceStep(0);
    setDocStep(0);
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (phase === 'idle') setPhase('voice');else if (phase === 'voice') setPhase('document');else if (phase === 'document') setPhase('success');else if (phase === 'success') resetAndLoop();
    }, PHASE_DURATIONS[phase]);
    return () => clearTimeout(timer);
  }, [phase, resetAndLoop]);
  useEffect(() => {
    if (phase !== 'voice') return;
    const t1 = setTimeout(() => setVoiceStep(1), 400);
    const t2 = setTimeout(() => setVoiceStep(2), 2200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [phase]);
  useEffect(() => {
    if (phase !== 'document') return;
    setDocStep(0);
    const t1 = setTimeout(() => setDocStep(1), 600);
    const t2 = setTimeout(() => setDocStep(2), 1800);
    const t3 = setTimeout(() => setDocStep(3), 2800);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [phase]);
  const getFieldStatus = (fieldIndex: number): FieldStatus => {
    if (fieldIndex === 0) {
      if (phase === 'idle') return 'empty';
      if (phase === 'voice' && voiceStep === 0) return 'active';
      if (phase === 'voice' && voiceStep >= 1) return voiceStep >= 2 ? 'done' : 'filling';
      return 'done';
    }
    if (fieldIndex === 1) {
      if (phase === 'idle') return 'empty';
      if (phase === 'voice' && voiceStep < 2) return 'empty';
      if (phase === 'voice' && voiceStep >= 2) return 'filling';
      return 'done';
    }
    if (phase === 'idle' || phase === 'voice') return 'empty';
    if (phase === 'document' && docStep === 0) return 'empty';
    if (phase === 'document' && docStep === 1) return 'active';
    if (phase === 'document' && docStep === 2) return 'filling';
    if (phase === 'document' && docStep >= 3) return 'done';
    return 'done';
  };
  const fieldStatuses = [getFieldStatus(0), getFieldStatus(1), getFieldStatus(2)];
  const activeFieldIndex = fieldStatuses.findIndex(s => s === 'active' || s === 'filling');
  const completedCount = fieldStatuses.filter(s => s === 'done').length;
  // Auto-scroll to the active field or success banner
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const scrollToEl = (el: HTMLElement | null) => {
      if (!el || !container) return;
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const offsetTop = elRect.top - containerRect.top + container.scrollTop;
      container.scrollTo({
        top: Math.max(0, offsetTop - 16),
        behavior: 'smooth'
      });
    };
    if (phase === 'success') {
      const t = setTimeout(() => scrollToEl(successRef.current), 150);
      return () => clearTimeout(t);
    }
    if (activeFieldIndex >= 0) {
      const t = setTimeout(() => scrollToEl(fieldRefs.current[activeFieldIndex]), 100);
      return () => clearTimeout(t);
    }
  }, [activeFieldIndex, phase]);
  const renderFormField = (index: number, label: string, placeholder: string, value: string, type: 'text' | 'file') => {
    const status = fieldStatuses[index];
    const isActive = status === 'active' || status === 'filling';
    const isDone = status === 'done';
    return <div className="mb-5" ref={el => {
      fieldRefs.current[index] = el;
    }} data-id="element-292">
        <label className="block text-[13px] font-medium text-gray-700 mb-1.5" data-id="element-293">
          {label}
        </label>
        <motion.div className={`relative rounded-lg border px-3.5 py-2.5 transition-colors duration-300 ${isActive ? 'border-orange-400 bg-white ring-2 ring-orange-100' : isDone ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50/50'}`} data-id="element-294">
          <div className="flex items-center justify-between min-h-[22px]" data-id="element-295">
            <div className="flex-1 min-w-0" data-id="element-296">
              {type === 'text' && status === 'filling' ? <TypingText text={value} className="text-[14px] text-gray-900" data-id="element-297" /> : type === 'file' && status === 'active' ? <motion.div className="flex items-center gap-2" initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3
            }} data-id="element-298">
                  <FileTextIcon size={15} className="text-orange-500 flex-shrink-0" data-id="element-299" />
                  <span className="text-[14px] text-gray-900" data-id="element-300">
                    drivers-license.pdf
                  </span>
                  <motion.div className="w-3.5 h-3.5 border-[1.5px] border-orange-400 border-t-transparent rounded-full ml-1" animate={{
                rotate: 360
              }} transition={{
                duration: 0.7,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-301" />
                </motion.div> : type === 'file' && status === 'filling' ? <motion.div className="flex items-center gap-2" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} data-id="element-302">
                  <FileTextIcon size={15} className="text-gray-600 flex-shrink-0" data-id="element-303" />
                  <span className="text-[14px] text-gray-900" data-id="element-304">
                    drivers-license.pdf
                  </span>
                </motion.div> : isDone && value ? <div className="flex items-center gap-2" data-id="element-305">
                  {type === 'file' && <FileTextIcon size={15} className="text-gray-600 flex-shrink-0" data-id="element-306" />}
                  <span className="text-[14px] text-gray-900" data-id="element-307">{value}</span>
                </div> : type === 'file' ? <div className="flex items-center gap-2 text-gray-400" data-id="element-308">
                  <UploadCloudIcon size={15} className="flex-shrink-0" data-id="element-309" />
                  <span className="text-[13px]" data-id="element-310">{placeholder}</span>
                </div> : <span className="text-[13px] text-gray-400" data-id="element-311">{placeholder}</span>}
            </div>

            <AnimatePresence mode="wait" data-id="element-312">
              {isDone && <motion.div initial={{
              scale: 0,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} exit={{
              scale: 0,
              opacity: 0
            }} transition={{
              type: 'spring',
              stiffness: 400,
              damping: 15
            }} className="ml-2 flex-shrink-0" data-id="element-313">
                  <CheckCircle2Icon size={16} className="text-green-500" data-id="element-314" />
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Extracted info for file field */}
          <AnimatePresence data-id="element-315">
            {type === 'file' && isDone && phase !== 'success' && <motion.div className="mt-2 pt-2 border-t border-gray-100 flex items-center gap-1.5" initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} transition={{
            duration: 0.3
          }} data-id="element-316">
                <SparklesIcon size={12} className="text-purple-500" data-id="element-317" />
                <span className="text-[11px] font-medium text-purple-600" data-id="element-318">
                  AI extracted 2 fields
                </span>
              </motion.div>}
          </AnimatePresence>
        </motion.div>
      </div>;
  };
  return <div className="bg-white flex flex-col w-full h-full relative overflow-hidden" style={{
    height: '420px'
  }} data-id="element-319">
      {/* Top app bar */}
      <div className="flex items-center justify-between px-3 sm:px-5 py-3 border-b border-gray-100 bg-white z-10" data-id="element-320">
        <div className="flex items-center gap-2 sm:gap-3" data-id="element-321">
          <div className="w-7 h-7 rounded-lg bg-orange-500 flex items-center justify-center" data-id="element-322">
            <FileTextIcon size={14} className="text-white" data-id="element-323" />
          </div>
          <div data-id="element-324">
            <h4 className="text-[13px] sm:text-[14px] font-semibold text-gray-900 leading-tight" data-id="element-325">
              KYC Onboarding
            </h4>
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-326">
          <div className={`w-1.5 h-1.5 rounded-full ${phase === 'success' ? 'bg-green-400' : phase === 'idle' ? 'bg-gray-300' : 'bg-orange-400'}`} data-id="element-327" />
          <span className="text-[11px] font-medium text-gray-400" data-id="element-328">
            {phase === 'idle' && 'Ready'}
            {phase === 'voice' && 'Recording'}
            {phase === 'document' && 'Processing'}
            {phase === 'success' && 'Complete'}
          </span>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-1 min-h-0 relative" data-id="element-329">
        {/* Left sidebar */}
        <div className="w-[160px] lg:w-[180px] border-r border-gray-100 bg-gray-50/60 py-4 px-2 flex-shrink-0 hidden sm:block" data-id="element-330">
          <div className="px-3 mb-1" data-id="element-331">
            <div className="flex items-center justify-between" data-id="element-332">
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider" data-id="element-333">
                Progress
              </span>
              <span className="text-[10px] font-medium text-gray-400" data-id="element-334">
                {completedCount}/3
              </span>
            </div>
            <div className="mt-1.5 h-1 bg-gray-200 rounded-full overflow-hidden mb-4" data-id="element-335">
              <motion.div className="h-full bg-orange-500 rounded-full" animate={{
              width: `${completedCount / 3 * 100}%`
            }} transition={{
              duration: 0.5,
              ease: 'easeOut'
            }} data-id="element-336" />
            </div>
          </div>

          <div className="mb-3" data-id="element-337">
            <div className="flex items-center gap-1.5 px-3 mb-2" data-id="element-338">
              <ChevronRightIcon size={12} className="text-gray-400" data-id="element-339" />
              <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-340">
                Applicant Info
              </span>
            </div>
            <div className="space-y-0.5" data-id="element-341">
              <SidebarFieldItem label="Full Name" status={fieldStatuses[0]} isActive={activeFieldIndex === 0} data-id="element-342" />
              <SidebarFieldItem label="Business Type" status={fieldStatuses[1]} isActive={activeFieldIndex === 1} data-id="element-343" />
              <SidebarFieldItem label="ID Document" status={fieldStatuses[2]} isActive={activeFieldIndex === 2} data-id="element-344" />
            </div>
          </div>

          <div data-id="element-345">
            <div className="flex items-center gap-1.5 px-3 mb-2" data-id="element-346">
              <ChevronRightIcon size={12} className="text-gray-300" data-id="element-347" />
              <span className="text-[11px] font-semibold text-gray-300 uppercase tracking-wider" data-id="element-348">
                Verification
              </span>
            </div>
            <div className="space-y-0.5 opacity-40" data-id="element-349">
              <SidebarFieldItem label="Address" status="empty" isActive={false} data-id="element-350" />
              <SidebarFieldItem label="Source of Funds" status="empty" isActive={false} data-id="element-351" />
            </div>
          </div>
        </div>

        {/* Right content area */}
        <div className="flex-1 flex flex-col min-w-0 relative bg-white" data-id="element-352">
          {/* Form content - Scrollable area */}
          <div ref={scrollContainerRef} className="px-3 sm:px-5 py-4 sm:py-5 flex-1 overflow-y-auto pb-24" data-id="element-353">
            <div className="mb-4 sm:mb-5" data-id="element-354">
              <h3 className="text-[14px] sm:text-[15px] font-semibold text-gray-900" data-id="element-355">
                Applicant Information
              </h3>
              <p className="text-[11px] sm:text-[12px] text-gray-400 mt-0.5" data-id="element-356">
                Field{' '}
                {Math.max(activeFieldIndex + 1, completedCount > 0 ? completedCount : 1)}{' '}
                of 3
              </p>
            </div>

            {renderFormField(0, 'Full Name', 'Enter full name...', 'Sarah Mitchell', 'text')}
            {renderFormField(1, 'Business Type', 'Enter business type...', 'Insurance Brokerage', 'text')}
            {renderFormField(2, 'ID Document', 'Drop file or click to upload', 'drivers-license.pdf', 'file')}

            {/* Success banner */}
            <AnimatePresence data-id="element-357">
              {phase === 'success' && <motion.div ref={successRef} className="mt-2 px-4 py-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3" initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -4
            }} transition={{
              type: 'spring',
              stiffness: 300,
              damping: 20
            }} data-id="element-358">
                  <motion.div animate={{
                boxShadow: ['0 0 0 0px rgba(34, 197, 94, 0.2)', '0 0 0 5px rgba(34, 197, 94, 0)']
              }} transition={{
                duration: 1,
                repeat: 1
              }} className="rounded-full flex-shrink-0" data-id="element-359">
                    <CheckCircle2Icon size={18} className="text-green-500" data-id="element-360" />
                  </motion.div>
                  <div data-id="element-361">
                    <p className="text-[12px] font-semibold text-green-800" data-id="element-362">
                      All fields validated
                    </p>
                    <p className="text-[11px] text-green-600" data-id="element-363">
                      Schema-aligned submission ready
                    </p>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Floating Action Bar / Footer */}
          <div className="absolute bottom-3 sm:bottom-6 left-0 right-0 flex flex-col items-center justify-end pointer-events-none px-3 sm:px-4" data-id="element-364">
            {/* Status Indicators (Toast style) */}
            <AnimatePresence data-id="element-365">
              {phase === 'voice' && <motion.div initial={{
              opacity: 0,
              y: 10,
              scale: 0.9
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: 10,
              scale: 0.9
            }} className="mb-2 sm:mb-3 bg-gray-900 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-xl flex items-center gap-2 sm:gap-3 pointer-events-auto" data-id="element-366">
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" data-id="element-367" />
                  <span className="text-[11px] sm:text-xs font-medium" data-id="element-368">
                    Listening...
                  </span>
                  <WaveformBars data-id="element-369" />
                </motion.div>}

              {phase === 'document' && docStep >= 1 && docStep < 3 && <motion.div initial={{
              opacity: 0,
              y: 10,
              scale: 0.9
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: 10,
              scale: 0.9
            }} className="mb-2 sm:mb-3 bg-white border border-gray-200 text-gray-900 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-xl flex items-center gap-2 sm:gap-3 pointer-events-auto" data-id="element-370">
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" data-id="element-371" />
                  <span className="text-[11px] sm:text-xs font-medium" data-id="element-372">
                    Uploading drivers-license.pdf...
                  </span>
                </motion.div>}
            </AnimatePresence>

            {/* Input Buttons */}
            <div className="bg-white rounded-full shadow-lg border border-gray-100 p-1 sm:p-1.5 flex items-center gap-1.5 sm:gap-2 pointer-events-auto" data-id="element-373">
              <motion.button className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-colors ${phase === 'voice' ? 'bg-orange-500 text-white' : 'bg-gray-900 text-white hover:bg-gray-800'}`} animate={phase === 'voice' ? {
              scale: [1, 1.1, 1]
            } : {
              scale: 1
            }} transition={phase === 'voice' ? {
              duration: 1.5,
              repeat: Infinity
            } : {}} data-id="element-374">
                <MicIcon size={16} className="sm:w-[18px] sm:h-[18px]" data-id="element-375" />
              </motion.button>

              <motion.button className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-colors ${phase === 'document' ? 'bg-blue-500 text-white' : 'bg-gray-900 text-white hover:bg-gray-800'}`} animate={phase === 'document' ? {
              y: [0, -4, 0]
            } : {
              y: 0
            }} transition={phase === 'document' ? {
              duration: 1.5,
              repeat: Infinity
            } : {}} data-id="element-376">
                <ArrowUpIcon size={16} className="sm:w-[18px] sm:h-[18px]" data-id="element-377" />
              </motion.button>

              <button className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gray-900 text-white flex items-center justify-center hover:bg-gray-800 transition-colors" data-id="element-378">
                <CameraIcon size={16} className="sm:w-[18px] sm:h-[18px]" data-id="element-379" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}